/* Hallmark · in-place rack registry · existing project tokens
 * pre-emit critique: P4 H4 E4 S5 R5 V3
 */
import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';
import { Database, Server, Zap, Weight, LayoutGrid, AlertCircle, Map } from 'lucide-react';
import { rackLabel } from '../utils/rackName.logic';

/* ─── Types ─────────────────────────────────────────────────────────────────── */

interface RackConfig {
  id: number | null;
  draft_ref?: string | null;
  draft_id?: number | null;
  draft_revision?: number | null;
  row_index: number;
  rack_index: number;
  x_m: number | null;
  y_m: number | null;
  rotation: number;
  type: string;
  height_m: number | null;
  u_slots: number | null;
  max_weight_kg?: number | null;
  max_power_w?: number | null;
  is_blocked: boolean;
  is_facility?: boolean;
  name?: string;
  label?: string | null;
  address_confirmed?: boolean;
  status?: string;
}

interface RegistryDraft { id: number; name: string; revision: number; published: boolean }

/* ─── Stat chip ──────────────────────────────────────────────────────────────── */

function StatChip({ icon: Icon, value, unit, dim }: {
  icon: React.ElementType;
  value: string | number;
  unit?: string;
  dim?: boolean;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-mono border ${
        dim
          ? 'border-[var(--border-color)] text-[var(--text-secondary)] bg-transparent'
          : 'border-[var(--border-focus)] text-[var(--text-secondary)] bg-[var(--bg-panel)]'
      }`}
    >
      <Icon size={10} strokeWidth={1.8} className={dim ? 'text-[var(--text-secondary)]' : 'text-[var(--accent)]'} />
      <span className="text-[var(--text-primary)]">{value}</span>
      {unit && <span className="text-[var(--text-secondary)]">{' '}{unit}</span>}
    </span>
  );
}

/* ─── Rack bento card ───────────────────────────────────────────────────────── */

function RackCard({ rack, dcCode }: {
  rack: RackConfig;
  dcCode: string;
}) {
  const title = rackLabel({ ...rack, address_confirmed: rack.address_confirmed === true });
  const subtitle = rack.address_confirmed === true ? `Ряд ${rack.row_index + 1}, Позиция ${rack.rack_index + 1}` : null;
  const unconfirmed = rack.address_confirmed !== true && !rack.is_facility && !['pillar', 'column'].includes(rack.type);
  const isBlocked = rack.is_blocked;
  const unpublished = rack.id === null;
  const facility = Boolean(rack.is_facility || ['crac', 'inrow_cooler', 'ups', 'pdu_frame', 'pillar'].includes(rack.type));
  const stateLabel = unpublished ? 'Не опубликована' : isBlocked ? 'Заблокирована'
    : ({ active: 'Активна', planned: 'Запланирована', reserved: 'Зарезервирована', deprecated: 'Выводится из эксплуатации' }[rack.status ?? ''] ?? 'Статус неизвестен');

  return (
    <article
      className={`group relative flex flex-col rounded-sm border bg-[var(--bg-panel)] overflow-hidden
        transition-all duration-200 ease-out
        hover:border-[var(--border-focus)]
        focus-within:border-[var(--border-focus)] focus-within:ring-1 focus-within:ring-[var(--border-focus)]
        ${isBlocked
          ? 'border-[var(--status-blocked)]'
          : 'border-[var(--border-color)]'
        }
        p-4 h-full
      `}
    >
      {/* Blocked overlay band */}
      {isBlocked && (
        <span className="absolute top-0 left-0 right-0 h-0.5 bg-[var(--status-blocked)]" aria-hidden />
      )}

      {/* Card top: title + status */}
      <div className="flex flex-col items-start gap-2 mb-3">
        {unpublished ? <h2 className="min-h-11 min-w-0 font-semibold text-sm wrap-anywhere text-[var(--text-primary)]">{title}</h2> : <Link
          to={`/${dcCode}/racks/${rack.id}`}
          className="group/link min-w-0 flex-1 min-h-11 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--text-primary)]"
          aria-label={`Открыть стойку: ${title}`}
        >
          <h2
            className="text-sm font-semibold leading-tight wrap-anywhere text-[var(--text-primary)] group-hover/link:text-[var(--accent)] transition-colors"
          >
            {title}
          </h2>
          {subtitle && (
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5 truncate">{subtitle}</p>
          )}
        </Link>}

        <span
          className={`max-w-full inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-sm font-medium border
            ${unpublished
              ? 'border-[var(--border-color)] text-[var(--text-secondary)]'
              : isBlocked
              ? 'border-[var(--status-blocked)] text-[var(--text-secondary)]'
              : 'border-[var(--border-color)] text-[var(--text-secondary)]'
            }`}
        >
          {stateLabel}
        </span>
      </div>

      {unconfirmed && <p className="text-xs text-[var(--text-secondary)]" title="Метка не является действующим адресом стойки.">Место не подтверждено</p>}
      {unpublished && <p className="mt-1 text-xs text-[var(--text-secondary)]">Монтаж и резервы недоступны до публикации.</p>}

      {/* Spacer — pushes chips to bottom */}
      <div className="flex-1" />

      {/* Stat chips row */}
      <div className="flex flex-wrap gap-1.5 mt-2">
        <StatChip
          icon={Server}
          value={facility ? 0 : rack.u_slots ?? 'U неизвестно'}
          unit={facility || rack.u_slots != null ? 'U' : undefined}
          dim={facility || rack.u_slots == null}
        />
        {rack.max_weight_kg != null ? (
          <StatChip icon={Weight} value={rack.max_weight_kg} unit="кг" />
        ) : (
          <StatChip icon={Weight} value="кг неизвестно" dim />
        )}
        {rack.max_power_w != null ? (
          <StatChip icon={Zap} value={rack.max_power_w} unit="Вт" />
        ) : (
          <StatChip icon={Zap} value="Вт неизвестно" dim />
        )}
      </div>

      {/* Footer: map link */}
      <div className="mt-3 pt-3 min-h-11 border-t border-[var(--border-color)] flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] font-mono text-[var(--text-secondary)] wrap-anywhere">
          {unpublished ? `Черновик #${rack.draft_id} · r${rack.draft_revision} · ${rack.draft_ref}` : `#${rack.id}`}
        </span>
        {!unpublished && <Link
          to={`/${dcCode}/map?rack=${rack.id}`}
          className="inline-flex min-h-11 items-center gap-1 text-[11px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--text-primary)] rounded-sm"
          aria-label={`Показать стойку ${title} на карте`}
        >
          <Map size={10} />
          {rack.draft_id != null ? 'В опубликованном плане' : 'На карте'}
        </Link>}
      </div>
    </article>
  );
}

/* ─── Skeleton card ─────────────────────────────────────────────────────────── */

function RackCardSkeleton() {
  return (
    <div
      className="rounded-sm border border-[var(--border-color)] bg-[var(--bg-panel)] flex flex-col p-4"
      aria-hidden
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1 space-y-1.5">
          <div className="h-3.5 w-28 skeleton rounded-sm" />
          <div className="h-2.5 w-20 skeleton rounded-sm" />
        </div>
        <div className="h-5 w-20 skeleton rounded-sm" />
      </div>
      <div className="flex-1" />
      <div className="flex gap-1.5 mt-2">
        <div className="h-4 w-12 skeleton rounded-sm" />
        <div className="h-4 w-14 skeleton rounded-sm" />
        <div className="h-4 w-16 skeleton rounded-sm" />
      </div>
      <div className="mt-3 pt-3 border-t border-[var(--border-color)] flex justify-between">
        <div className="h-3 w-6 skeleton rounded-sm" />
        <div className="h-3 w-14 skeleton rounded-sm" />
      </div>
    </div>
  );
}

/* ─── Summary stats bar ─────────────────────────────────────────────────────── */

function SummaryBar({ racks }: { racks: RackConfig[] }) {
  const itRacks = racks.filter(r => !r.is_facility && !['crac', 'inrow_cooler', 'ups', 'pdu_frame', 'pillar'].includes(r.type));
  const total = itRacks.length;
  const active = itRacks.filter(r => r.id !== null && !r.is_blocked && r.status === 'active').length;
  const blocked = itRacks.filter(r => r.is_blocked).length;
  const totalU = itRacks.every(r => r.u_slots != null) ? itRacks.reduce((s, r) => s + r.u_slots!, 0) : null;
  const totalPowerKw = itRacks.every(r => r.max_power_w != null) ? itRacks.reduce((s, r) => s + r.max_power_w!, 0) / 1000 : null;

  const stats = [
    { label: 'Всего стоек', value: total, mono: false },
    { label: 'Активных', value: active, mono: false, highlight: true },
    { label: 'Не опубликовано', value: itRacks.filter(r => r.id === null).length, mono: false },
    { label: 'Заблокировано', value: blocked, mono: false, dim: blocked === 0 },
    { label: 'Юнитов (суммарно)', value: totalU === null ? 'Неизвестно' : `${totalU}U`, mono: true },
    { label: 'Мощность (макс.)', value: totalPowerKw === null ? 'Неизвестно' : `${totalPowerKw.toFixed(1)} кВт`, mono: true },
  ];

  return (
    <div className="flex flex-wrap gap-px border border-[var(--border-color)] rounded-sm overflow-hidden mb-6">
      {stats.map((s, i) => (
        <div
          key={i}
          className="flex-1 min-w-[136px] px-4 py-2.5 bg-[var(--bg-panel)] flex flex-col gap-0.5"
        >
          <span className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)] font-medium">{s.label}</span>
          <span
            className={`text-base font-bold leading-tight wrap-anywhere ${
              s.mono ? 'font-mono text-[var(--text-secondary)]' :
              s.highlight ? 'text-[var(--status-normal)]' :
              s.dim ? 'text-[var(--text-muted)]' :
              'text-[var(--text-primary)]'
            }`}
          >
            {s.value}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── Main page ─────────────────────────────────────────────────────────────── */

export default function RacksListPage() {
  const { dcCode } = useParams<{ dcCode: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDraft = searchParams.get('draft');
  const [drafts, setDrafts] = useState<RegistryDraft[]>([]);
  const [draftsError, setDraftsError] = useState(false);
  const [racks, setRacks] = useState<RackConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDrafts([]);
    setDraftsError(false);
    if (dcCode) api.get(`/api/datacenters/${dcCode}/drafts`)
      .then(res => { if (!cancelled) setDrafts(res.data.filter((draft: RegistryDraft) => !draft.published)); })
      .catch(() => { if (!cancelled) setDraftsError(true); });
    return () => { cancelled = true; };
  }, [dcCode]);

  useEffect(() => {
    let cancelled = false;
    if (!dcCode) {
      setRacks([]);
      return;
    }
    setLoading(true);
    setRacks([]);
    setError(null);
    api.get(`/api/datacenters/${dcCode}/racks`, {
      params: selectedDraft !== null ? { draft_id: selectedDraft } : { workspace: 'master' }
    })
      .then(res => { if (!cancelled) setRacks(res.data); })
      .catch(() => { if (!cancelled) setError(selectedDraft !== null
        ? 'Не удалось загрузить черновик. Опубликованные данные не подставлены.'
        : 'Не удалось загрузить стойки. Проверьте соединение.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dcCode, selectedDraft]);

  return (
    <div className="h-full flex flex-col bg-[var(--bg-app)] text-[var(--text-primary)]">

      {/* ── Page header ── */}
      <header className="px-4 sm:px-6 py-4 border-b border-[var(--border-color)] flex flex-wrap items-center justify-between gap-3 shrink-0">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2.5">
            <LayoutGrid size={20} className="text-[var(--accent)]" aria-hidden />
            Стойки
          </h1>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">
            Реестр стоечного оборудования ЦОД
          </p>
        </div>

        {dcCode && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--text-muted)] min-w-0 max-w-full">
            <label className="flex flex-wrap items-center gap-2 min-w-0 max-w-full">
              План реестра
              <select aria-label="План реестра" value={selectedDraft ?? 'master'} className="min-h-11 min-w-0 max-w-full bg-[var(--bg-panel)] border border-[var(--border-color)] rounded-sm px-2 text-[var(--text-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--text-primary)]"
                onChange={event => {
                  const params = new URLSearchParams(searchParams);
                  if (event.target.value === 'master') {
                    params.delete('draft');
                  } else params.set('draft', event.target.value);
                  setSearchParams(params);
                }}>
                <option value="master">Опубликованный план</option>
                {selectedDraft !== null && !drafts.some(draft => String(draft.id) === selectedDraft) && <option value={selectedDraft}>Черновик #{selectedDraft || '—'}</option>}
                {drafts.map(draft => <option key={draft.id} value={draft.id}>{draft.name} · #{draft.id}</option>)}
              </select>
            </label>
            {draftsError && <span role="status">Список черновиков недоступен</span>}
            <Link
              to={`/${dcCode}/floor-plan`}
              className="flex min-h-11 items-center gap-1.5 px-2.5 py-1 rounded-sm border border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--accent)] bg-[var(--bg-panel)] transition-colors font-medium"
              title="Открыть план зала"
            >
              <LayoutGrid size={14} className="text-[var(--accent)]" />
              План зала
            </Link>
            <span className="max-w-full wrap-anywhere px-2 py-1 rounded-sm border border-[var(--border-color)] font-mono text-[var(--text-secondary)] bg-[var(--bg-panel)]">
              {dcCode}
            </span>
          </div>
        )}
      </header>
      {selectedDraft !== null && <p role="status" className="px-4 sm:px-6 py-3 border-b border-[var(--border-color)] text-xs text-[var(--text-secondary)]">
        Просмотр черновика. Физическое размещение не изменено. Ссылки опубликованных стоек открывают их текущее состояние, а не проектные изменения.
      </p>}

      {/* ── Body ── */}
      <div className="flex-1 overflow-auto">
        {!dcCode ? (
          /* No DC selected */
          <div className="flex flex-col items-center justify-center h-full gap-3 text-[var(--text-muted)]">
            <Database size={40} strokeWidth={1} className="opacity-30" />
            <p className="text-sm">Выберите ЦОД в меню навигации, чтобы просмотреть стойки.</p>
          </div>
        ) : error ? (
          /* Error state */
          <div className="flex flex-col items-center justify-center h-full gap-3 text-[var(--status-critical)]">
            <AlertCircle size={36} strokeWidth={1.5} className="opacity-60" />
            <p className="text-sm text-center max-w-xs">{error}</p>
          </div>
        ) : (
          <div className="p-4 sm:p-6">
            {/* Summary bar */}
            {loading
              ? <div className="h-14 skeleton rounded-sm mb-6" />
              : racks.length > 0 && <SummaryBar racks={racks} />
            }

            {/* Bento grid */}
            {loading ? (
              /* Skeleton grid */
              <div
                className="grid gap-3"
                style={{
                  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))',
                  gridAutoRows: 'auto',
                }}
                aria-busy="true"
                aria-label="Загрузка стоек…"
              >
                {Array.from({ length: 9 }, (_, i) => (
                  <div
                    key={i}
                  >
                    <RackCardSkeleton />
                  </div>
                ))}
              </div>
            ) : racks.length === 0 ? (
              /* Empty state */
              <div className="flex flex-col items-center justify-center py-20 gap-4 text-[var(--text-muted)]">
                <div
                  className="w-16 h-16 rounded-sm border border-[var(--border-color)] flex items-center justify-center bg-[var(--bg-panel)]"
                  aria-hidden
                >
                  <Database size={28} strokeWidth={1} className="opacity-40" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-[var(--text-secondary)]">Стоек пока нет</p>
                  <p className="text-xs mt-1">
                    Добавьте стойки через{' '}
                    <Link
                      to={`/${dcCode}/calculator`}
                      className="text-[var(--accent)] hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--border-focus)] rounded-sm"
                    >
                      2D Редактор
                    </Link>
                  </p>
                </div>
              </div>
            ) : (
              /* Bento grid — racks */
              <div
                role="list"
                aria-label={`Стойки ЦОД ${dcCode}`}
                className="grid gap-3"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))' }}
              >
                {racks.map(rack => {
                  return (
                    <div
                      key={rack.id ?? `${rack.draft_id}:${rack.draft_revision}:${rack.draft_ref}`}
                      role="listitem"
                    >
                      <RackCard rack={rack} dcCode={dcCode} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
